import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import { Table, Toast, ToastHeader, ToastBody, Row, Col, Nav, NavItem, NavLink, TabContent, TabPane, Card, CardTitle } from 'reactstrap';
import Page from '../Page';
import { useAppSelector, useAppDispatch } from '../../hooks/index';
import { Entity, getSelectedEntity } from '../../redux/EntitySlice';
import { Fact, Afr, getSelectedAfr, ValidationError, getDimensionForFact, getMissingFacts, AfrVersion } from '../../redux/AfrSlice';
import { setIsDirty, isAdmin, getDimensionSectionMap } from '../../redux/MiscSlice';
import { FactSectionMap, addUnassignedUASFact } from '../../redux/ValidationSlice';
import NavigationPanelItem from '../core/NavigationPanelItem';
import DiscrepancyEmail from '../feature/DiscrepancyEmail';
import ErrorFactList from '../feature/ErrorFactList';
import ValidationErrorList from '../feature/ValidationErrorList';
import { BypassStatus, EmailEventInput, getDiscrepancyEvents, DiscrepancyEvent, loadEvents, AfrEvents, getSelectedEvents, postDiscrepancy, getMessages } from '../../redux/EventsSlice';
import { generateBypassSet } from '../feature/BypassRequest';
import { TaxonomyTypeEnum } from '../../redux/TaxonomyVersionSlice';
import utilityHelper from '../../utils/utilityHelper';
import { Concept } from "../../redux/ConceptSlice";

const ErrorsCore = (props: { userRole: string, taxType: TaxonomyTypeEnum, reportName : string }) => {

    const errorFactsBySection : FactSectionMap = {
            balanceSheet: [],
            revenues: [],
            expenditures: [],
            longTermDebt: [],
            audit: [],
            otherAfrData: [],
            affiliatedEntities: [],
            courtInnovations: [],
            legalAidProgram: [],
            lawLibrary: [],
            juvenileAlternativeProgram: [],
            partA: [],
            partB: [],
            partC: [],
            partD: [],
            partE: [],
            governmental: [],
            proprietary: [],
            fiduciary: [],
            componentUnits: [],
            invalid: []
        };
    const [errorFactSectionMap, setErrorFactSectionMap] = useState<FactSectionMap>(errorFactsBySection);

    const [discrepancies, setDiscrepancies] = useState<DiscrepancyEvent | null>(null);
    const [importErrors, setImportErrors] = useState<ValidationError[] | null>(null);
    var bypasses: Array<BypassStatus> = [];
    const [bypassList, setBypassList] = useState(bypasses);

    const currentAfr: Afr | null = useAppSelector(getSelectedAfr);
    const missingFacts: Fact[] = useAppSelector(getMissingFacts);
    var afrMessages = useAppSelector(getMessages);
    
    const afrYear = currentAfr ? currentAfr.year : 0;
    const logicalPeriod = currentAfr ?
        currentAfr.logicalPeriod ? currentAfr.logicalPeriod.replace("Q_", "Quarter ").replace("ANNUAL","") : ""
        : "";
    const entity: Entity | null = useAppSelector(getSelectedEntity);
    const currentAfrEvents: AfrEvents | null = useAppSelector(getSelectedEvents);
    const ecode = entity ? entity.code : "";
    const afrId = ecode + "," + afrYear;

    const isAdminUser: boolean = useAppSelector(isAdmin);
    const dmap = useAppSelector(getDimensionSectionMap);

    function getDisplayDimensions(f : Fact) {
        var dimValues : string[] = [];
        for (var dr of f.dimensions) {
            if (dr.member !== null) {
                dimValues.push(dr.axis.name + "=>" + dr.member.name);
            }           
        }
        return dimValues;
    }

    useEffect(function () {
        setBypassList(generateBypassSet(afrMessages));

    }, [afrMessages]);


    useEffect(function () {
        setDiscrepancies(getDiscrepancyEvents(currentAfr, entity, currentAfrEvents));
    }, [currentAfr, entity, currentAfrEvents]);

    var defaultAddUnassignedFact = function (f: Fact, av: AfrVersion, sMap: FactSectionMap, dmap: any) {
        if (f.concept.id.indexOf("LongTermDebt") >= 0) {
            sMap.longTermDebt.push(f);
        } else if (getDimensionForFact(f, "ReportingUnitAxis")) {
            sMap.affiliatedEntities.push(f);
        } else if (getDimensionForFact(f, "AfrNoDataAxis")) {
            var member = getDimensionForFact(f, "AfrNoDataAxis");
            if (member) {
                switch (member.id) {
                    case "AFRND_ExpendituresMember":
                        sMap.expenditures.push(f);
                        break;

                    case "AFRND_RevenuesMember":
                        sMap.revenues.push(f);
                        break;

                    case "AFRND_BalanceSheetMember":
                        sMap.balanceSheet.push(f);
                        break;

                    case "AFRND_GovernmentalMember":
                        sMap.governmental.push(f);
                        break;

                    case "AFRND_ProprietaryMember":
                        sMap.proprietary.push(f);
                        break;

                    case "AFRND_FiduciaryMember":
                        sMap.fiduciary.push(f);
                        break;

                    case "AFRND_DiscreteComponentUnitsMember":
                        sMap.componentUnits.push(f);
                        break;

                    case "AFRND_ConstructionProjectsMember":
                        sMap.otherAfrData.push(f);
                        break;

                    case "AFRND_Bonds":
                        sMap.otherAfrData.push(f);
                        break;

                    case "AFRND_DRAuditMember":
                    case "ADRND_DRImpactFeeExceptionMember":
                    case "ADRND_DRImpactFeeWaiverMember":
                    case "ADRND_DRImpactFeeAffidavitMember":
                    case "ADRND_DRBudgetVarianceReportMember":
                    case "ADRND_DRCR29SignedLetterMember":
                    case "ADRND_DRCR29CPALetterMember":
                        sMap.otherAfrData.push(f);
                        break;

                    default:
                        sMap.otherAfrData.push(f);
                        break;
                }
            }
        } else {
            sMap.otherAfrData.push(f);
        }
    }
    const addUnassignedFacts = function (afr: Afr, sMap: FactSectionMap, uFacts: Fact[]) {
        if (!afr.currentversion) {
            return;
        }
        var av: AfrVersion = afr.currentversion;
        for (var f of uFacts) {
            if (getDimensionForFact(f, "ReportingUnitAxis")) {
                sMap.affiliatedEntities.push(f);
                continue;
            }
            if (f.concept.id.indexOf("Audit") >= 0) {
                sMap.audit.push(f);
                continue;
            }
            if (f.concept.id.indexOf("BalanceSheetTitle") >= 0) {
                sMap.balanceSheet.push(f);
                continue;
            }
            if (afr.year >= 2024) {
                var fundGroup: Concept | any = getDimensionForFact(f, "FundGroupsAxis");
                if (fundGroup) {
                    var identifier = fundGroup.name.split(" ")[0];
                    switch (identifier) {
                        case "001":
                        case "050":
                        case "100":
                        case "200":
                        case "300":
                            sMap.governmental.push(f);
                            break;
                        case "600":
                        case "650":
                        case "700":
                        case "750":
                            sMap.fiduciary.push(f);
                            break;
                        case "400":
                        case "500":
                            sMap.proprietary.push(f);
                            break;
                        case "001":
                            sMap.componentUnits.push(f);
                            break;
                    }
                    continue;
                }
            }
            var firstDigit = ( f.accountCode && f.accountCode.length > 0 ) ? f.accountCode.substring(0, 1) : "I";
            switch (firstDigit) {
                case "1":
                case "2":
                    addUnassignedUASFact(afr, sMap, f, sMap.balanceSheet);
                    break;

                case "3":
                case "4":
                    if (getDimensionForFact(f, "ReportingUnitAxis")) {
                        sMap.affiliatedEntities.push(f);
                    } else {
                        addUnassignedUASFact(afr, sMap, f, sMap.revenues);
                    }
                    break;
                case "5":
                case "6":
                case "7":
                    if (getDimensionForFact(f, "ReportingUnitAxis")) {
                        sMap.affiliatedEntities.push(f);
                    } else {
                        addUnassignedUASFact(afr, sMap, f, sMap.expenditures);
                    }
                    break;
                case "I":
                    sMap.invalid.push(f);
                    break;

                case "C":
                    if (f.accountCode.indexOf("CR_939") === 0) {
                        const sect: string = utilityHelper.getSectionForDimension(f, dmap);
                        switch (sect) {
                            case "CR_939_CourtInnovations":
                                sMap.courtInnovations.push(f);
                                break;
                            case "CR_939_LegalAidProgram":
                                sMap.legalAidProgram.push(f);
                                break;
                            case "CR_939_LawLibrary":
                                sMap.lawLibrary.push(f);
                                break;
                            case "CR_939_JuvenileAlternativeProgram":
                                sMap.juvenileAlternativeProgram.push(f);
                                break;
                            default:
                                sMap.invalid.push(f)
                                break;
                        }
                    } else if (f.accountCode.indexOf("CR_29") === 0) {
                        const tname: string = utilityHelper.getTableNameForFact(av, f)
                        switch (tname) {
                            case "cr29-court-reporting-local-requirements-collections":
                                sMap.partA.push(f);
                                break;
                            case "cr29-court-reporting-non-recurring-expenditures":
                                sMap.partB.push(f);
                                break;
                            case "cr29-court-reporting-facility-cost":
                                sMap.partC.push(f);
                                break;
                            case "cr29-court-reporting-communications-systems-and-services":
                                sMap.partC.push(f);
                                break;
                            case "cr29-court-reporting-existing-systems":
                                sMap.partC.push(f);
                                break;
                            case "cr29-court-reporting-local-requirements":
                                sMap.partD.push(f);
                                break;
                            case "cr29-court-reporting-other-operating-court-related-expenditures":
                                sMap.partE.push(f);
                                break;
                            default:
                                sMap.invalid.push(f)
                                break;
                        }

                    } else {
                        defaultAddUnassignedFact(f, av, sMap, dmap);
                    }
                    break;

               default:
                    defaultAddUnassignedFact(f, av, sMap, dmap);
                    break;
            }
        }
    }
    useEffect(function () {
        var sMap: FactSectionMap = {
            balanceSheet: [],
            revenues: [],
            expenditures: [],
            longTermDebt: [],
            audit: [],
            otherAfrData: [],
            affiliatedEntities: [],
            courtInnovations: [],
            legalAidProgram: [],
            lawLibrary: [],
            juvenileAlternativeProgram: [],
            partA: [],
            partB: [],
            partC: [],
            partD: [],
            partE: [],
            governmental: [],
            proprietary: [],
            fiduciary: [],
            componentUnits: [],
            invalid: []
        };
        if (currentAfr && currentAfr.currentversion) {
            if (currentAfr.currentversion.tables) {
                currentAfr.currentversion.tables.map(t =>
                    t.rows.map(r =>
                        addUnassignedFacts(currentAfr, sMap, r.facts.filter(f => f.validationerrors != null && f.validationerrors.length > 0))
                    )
                )
            }
            if (currentAfr.currentversion.unassignedFacts) {
                addUnassignedFacts(currentAfr, sMap, currentAfr.currentversion.unassignedFacts);
            }
            if (missingFacts) {
                addUnassignedFacts(currentAfr, sMap, missingFacts);
            }
            setImportErrors(currentAfr.currentversion.validationerrors);
        } else {
            setImportErrors(null);
        }
        setErrorFactSectionMap(sMap);

    }, [currentAfr,missingFacts]);


    return (
        <React.Fragment>
            <Page>
                {entity != null ? (
                    <h2>Errors in {props.reportName} Data for {utilityHelper.getEntityName(entity, currentAfr)} ({utilityHelper.getEntityCode(entity, currentAfr)}), {afrYear} {logicalPeriod} </h2>) : (<></>)}
                
                <Row>
                    <Col sm="12">
                        {discrepancies && discrepancies.firstDiscrepancy && (
                            <Row>
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Discrepancy Errors">

                                            <DiscrepancyEmail allowEdit={false} />

                                        </NavigationPanelItem>
                                    </React.Fragment>
                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.governmental.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Governmental Funds Errors">
                                            <ErrorFactList afrId={afrId} bypassList={bypassList} listName="Unassigned Governmental Funds facts" factList={errorFactSectionMap.governmental} />
                                        </NavigationPanelItem>
                                    </React.Fragment>
                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.proprietary.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Proprietary Funds Errors">
                                            <ErrorFactList afrId={afrId} bypassList={bypassList} listName="Unassigned Proprietary Funds facts" factList={errorFactSectionMap.proprietary} />
                                        </NavigationPanelItem>
                                    </React.Fragment>
                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.fiduciary.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Fiduciary Funds Errors">
                                            <ErrorFactList afrId={afrId} bypassList={bypassList} listName="Unassigned Fiduciary Funds facts" factList={errorFactSectionMap.fiduciary} />
                                        </NavigationPanelItem>
                                    </React.Fragment>
                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.componentUnits.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Discrete Component Units Funds Errors">
                                            <ErrorFactList afrId={afrId} bypassList={bypassList} listName="Unassigned Discrete Component Units Funds facts" factList={errorFactSectionMap.componentUnits} />
                                        </NavigationPanelItem>
                                    </React.Fragment>
                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.balanceSheet.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Balance Sheet Errors">
                                            <ErrorFactList afrId={afrId} bypassList={bypassList} listName="Unassigned Balance Sheet facts" factList={errorFactSectionMap.balanceSheet} />
                                        </NavigationPanelItem>
                                    </React.Fragment>
                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.revenues.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Revenues Errors">
                                            <ErrorFactList afrId={afrId} bypassList={bypassList} listName="Unassigned Revenue facts" factList={errorFactSectionMap.revenues} />
                                        </NavigationPanelItem>
                                    </React.Fragment>
                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.expenditures.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Expenditures Errors">
                                            <ErrorFactList afrId={afrId} bypassList={bypassList} listName="Unassigned Expenditure facts" factList={errorFactSectionMap.expenditures} />
                                        </NavigationPanelItem>
                                    </React.Fragment>

                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.longTermDebt.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Long Term Debt">
                                            <ErrorFactList afrId={afrId} bypassList={bypassList} listName="Long Term Debt" factList={errorFactSectionMap.longTermDebt} />
                                        </NavigationPanelItem>
                                    </React.Fragment>

                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.audit.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Audit">
                                            <ErrorFactList afrId={afrId} bypassList={bypassList} listName="Audit" factList={errorFactSectionMap.audit} />
                                        </NavigationPanelItem>
                                    </React.Fragment>

                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.otherAfrData.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Other Data Errors">
                                            <ErrorFactList afrId={afrId} bypassList={bypassList} listName="Other Data facts" factList={errorFactSectionMap.otherAfrData} />
                                        </NavigationPanelItem>
                                    </React.Fragment>

                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.affiliatedEntities.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Affiliated Entities Data Errors">
                                            <ErrorFactList afrId={afrId} bypassList={bypassList} listName="Affiliated Entities Data facts" factList={errorFactSectionMap.affiliatedEntities} />
                                        </NavigationPanelItem>
                                    </React.Fragment>

                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.courtInnovations.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Court Innovations">
                                            <ErrorFactList afrId={afrId} bypassList={[]} listName="Court Innovations facts" factList={errorFactSectionMap.courtInnovations} />
                                        </NavigationPanelItem>
                                    </React.Fragment>

                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.legalAidProgram.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Legal Aid Program">
                                            <ErrorFactList afrId={afrId} bypassList={[]} listName="Legal Aid Program facts" factList={errorFactSectionMap.legalAidProgram} />
                                        </NavigationPanelItem>
                                    </React.Fragment>

                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.lawLibrary.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Law Library">
                                            <ErrorFactList afrId={afrId} bypassList={[]} listName="Law Library facts" factList={errorFactSectionMap.lawLibrary} />
                                        </NavigationPanelItem>
                                    </React.Fragment>

                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.juvenileAlternativeProgram.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Juvenile Alternative Program">
                                            <ErrorFactList afrId={afrId} bypassList={[]} listName="Juvenile Alternative Program facts" factList={errorFactSectionMap.juvenileAlternativeProgram} />
                                        </NavigationPanelItem>
                                    </React.Fragment>

                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.partA.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Part A">
                                            <ErrorFactList afrId={afrId} bypassList={[]} listName="Juvenile Alternative Program facts" factList={errorFactSectionMap.partA} />
                                        </NavigationPanelItem>
                                    </React.Fragment>

                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.partB.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Part B">
                                            <ErrorFactList afrId={afrId} bypassList={[]} listName="Juvenile Alternative Program facts" factList={errorFactSectionMap.partB} />
                                        </NavigationPanelItem>
                                    </React.Fragment>

                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.partC.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Part C">
                                            <ErrorFactList afrId={afrId} bypassList={[]} listName="Juvenile Alternative Program facts" factList={errorFactSectionMap.partC} />
                                        </NavigationPanelItem>
                                    </React.Fragment>

                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.partD.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Part D">
                                            <ErrorFactList afrId={afrId} bypassList={[]} listName="Juvenile Alternative Program facts" factList={errorFactSectionMap.partD} />
                                        </NavigationPanelItem>
                                    </React.Fragment>

                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.partE.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Part E">
                                            <ErrorFactList afrId={afrId} bypassList={[]} listName="Juvenile Alternative Program facts" factList={errorFactSectionMap.partE} />
                                        </NavigationPanelItem>
                                    </React.Fragment>

                                </Col>
                            </Row>
                        )}
                        {(errorFactSectionMap.invalid.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Invalid Account Errors">
                                            <ErrorFactList afrId={afrId} bypassList={bypassList} listName="Invalid accounts" factList={errorFactSectionMap.invalid} />
                                        </NavigationPanelItem>
                                    </React.Fragment>

                                </Col>
                            </Row>
                        )}
                        {(importErrors && importErrors.length > 0) && (
                            <Row className="my-1">
                                <Col>
                                    <React.Fragment>
                                        <NavigationPanelItem
                                            active={true}
                                            open={false}
                                            backColor="#f8d7da"
                                            color="#721c24"
                                            componentName="Import Errors">
                                            <ValidationErrorList title="Import errors" errorsToShow={importErrors} />
                                        </NavigationPanelItem>
                                    </React.Fragment>

                                </Col>
                            </Row>
                        )}
                    </Col>
                </Row>

            </Page>
        </React.Fragment>
    );
}

export default ErrorsCore;